import { StreamChat } from 'stream-chat';

/**
 * Connect the GetStream user, watch the classroom channel, and listen for
 * message.new / message.updated / message.deleted events.
 */
export async function subscribeCommunityChannelRealtime({
  apiKey,
  userId,
  userName,
  token,
  channelId,
  channelType = 'messaging',
  handlers = {},
}) {
  const client = StreamChat.getInstance(apiKey);
  const rawCid = String(channelId);
  const cid = rawCid.includes(':') ? rawCid.split(':').pop() : rawCid;
  const fullCid = rawCid.includes(':') ? rawCid : `${channelType}:${cid}`;

  if (!client.userID || String(client.userID) !== String(userId)) {
    if (client.userID) {
      try {
        await client.disconnectUser();
      } catch {
        // ignore prior disconnect errors
      }
    }
    await client.connectUser({ id: String(userId), name: userName || 'User' }, String(token));
  }

  const channel = client.channel(channelType, cid);
  const matchesChannel = (event) => {
    const eventChannelId = event.channel_id || event.channel?.id || event.cid?.split(':').pop() || '';
    const eventCid = event.cid || event.channel?.cid || '';
    return String(eventChannelId) === cid || String(eventCid) === fullCid;
  };

  // Attach listeners before watch() so a message arriving during the initial
  // channel query cannot fall between the snapshot and event subscription.
  // Client events are filtered by CID; subscribing on both client and channel
  // delivers the same event twice.
  const subscriptions = [
    client.on('message.new', (event) => {
      if (!matchesChannel(event)) return;
      handlers.onNew?.(event);
    }),
    client.on('message.updated', (event) => {
      if (!matchesChannel(event)) return;
      handlers.onUpdated?.(event);
    }),
    client.on('message.deleted', (event) => {
      if (!matchesChannel(event)) return;
      handlers.onDeleted?.(event);
    }),
    client.on('connection.recovered', () => handlers.onRecovered?.()),
  ];

  let initialMessages;
  try {
    const response = await channel.watch({ state: true, presence: false });
    initialMessages = response?.messages || channel.state.messages || [];
  } catch (error) {
    subscriptions.forEach((sub) => sub.unsubscribe());
    throw error;
  }

  return {
    initialMessages,
    unsubscribe: () => {
      subscriptions.forEach((sub) => {
        try {
          sub.unsubscribe();
        } catch {
          // ignore
        }
      });
      channel.stopWatching().catch(() => {});
    },
  };
}

export async function disconnectCommunityStreamUser() {
  const apiKey = process.env.REACT_APP_GET_STREAM_APP_KEY || '';
  if (!apiKey) return;
  try {
    const client = StreamChat.getInstance(apiKey);
    if (client.userID) {
      await client.disconnectUser();
    }
  } catch {
    // ignore logout cleanup failures
  }
}
